import { isEmpty, isEqual, omit } from 'lodash-es'

import {
  activeValue,
  allValue,
  MEETINGS_PRESET_PARAM,
  MeetingRoles,
  myMeetingsValue,
  Periods,
  permissionValue,
  UPCOMING_PRESET_OFF,
  UPCOMING_PRESET_PARAM,
} from 'constants/meetings'
import { useScope } from 'hooks/meetings/use_scope'
import { camelize, snakeize } from 'services/string_utils'

/**
 * Checks if the scope represents the current user's "My Calendly" scope
 * @param {string} scopeType - The scope type (e.g., 'user', 'team')
 * @param {string} scopeId - The scope ID (e.g., 'me', 'user123')
 * @returns {boolean} True if this is the current user's scope
 */
const isMyCalendlyScope = (scopeType, scopeId) => scopeType === 'user' && scopeId === 'me'

export const useIsMyCalendlyScope = () => {
  const { scopeType, scopeId } = useScope()

  return isMyCalendlyScope(scopeType, scopeId)
}

/**
 * Converts host_ids filter values to MeetingRoles enum values
 * @param {Array} hostIds - Array of host_id values (e.g., ['my_meetings'], ['non_host'], ['all'])
 * @param {string} scopeType - The scope type (e.g., 'user', 'team')
 * @param {string} scopeId - The scope ID (e.g., 'me', 'user123')
 * @returns {Array|undefined} Array of MeetingRoles values or undefined if no roles should be set
 */
export const convertHostIdsToMeetingRoles = (hostIds, scopeType, scopeId) => {
  // TODO: try removing show_non_organizer_meetings and update my_calendly scope to use attendee. Should be same thing without FF
  if (isEmpty(hostIds)) {
    if (isMyCalendlyScope(scopeType, scopeId)) {
      return [MeetingRoles.HOST, MeetingRoles.COHOST, MeetingRoles.PERMISSION]
    }
    return undefined
  }

  if (hostIds.includes(allValue)) {
    if (isMyCalendlyScope(scopeType, scopeId)) {
      return [MeetingRoles.HOST, MeetingRoles.COHOST, MeetingRoles.PERMISSION]
    }
    return [MeetingRoles.ATTENDEE, MeetingRoles.PERMISSION]
  }

  if (hostIds.includes(myMeetingsValue)) {
    if (isMyCalendlyScope(scopeType, scopeId)) {
      return [MeetingRoles.HOST, MeetingRoles.COHOST]
    }
    return [MeetingRoles.ATTENDEE]
  }

  if (hostIds.includes(permissionValue)) {
    return [MeetingRoles.PERMISSION]
  }

  return undefined
}

const mapHostIdsToRole = (filterParams, filters, scopeType, scopeId) => {
  const roles = convertHostIdsToMeetingRoles(filterParams.host_ids, scopeType, scopeId)
  if (roles) {
    filters.role = roles
  }
}


export const buildFilters = (
  scopeType,
  scopeId,
  selectedView,
  filterParams,
  isFromMeetings = false,
  allowedScopeTypes = [],
  mapScopeTypeToBackendValue = null
) => {
  const filters = camelize({
    ...filterParams,
    scopeType,
    scopeId,
    selectedView,
    period: filterParams.period,
    order: 'ASC',
  })

  // Add date range if both start and end dates are provided
  if (filterParams.start_date && filterParams.end_date) {
    filters.startDate = filterParams.start_date
    filters.endDate = filterParams.end_date
  }

  if (isFromMeetings) mapHostIdsToRole(filterParams, filters, scopeType, scopeId)

  Object.keys(filters).forEach((key) => {
    if (!filters[key] || (Array.isArray(filters[key]) && filters[key].length === 0)) {
      delete filters[key]
    }
  })

  if (filters?.scopeType && mapScopeTypeToBackendValue) {
    filters.scopeType = mapScopeTypeToBackendValue(filters.scopeType, filters?.scopeId)
    filters.listMeetingsScopeType = filters.scopeType

    if (!allowedScopeTypes.includes(filters.scopeType)) {
      filters.scopeType = ''
    }

    if (filters.scopeType === 'my_calendly') {
      filters.scopeId = ''
    }
  }

  if (scopeType === 'users' && !scopeId) {
    filters.ownerType = 'USER'
  } else if (scopeType === 'teams' && !scopeId) {
    filters.ownerType = 'TEAM'
  }

  return filters
}

export const resetParams = (isMeetingsPage, isMyCalendlyScopeValue, searchKeyword) => ({
  period: isMeetingsPage ? Periods.UPCOMING : Periods.PAST,
  status_ids: [activeValue],
  status_explicitly_set: false,
  event_type_ids: [],
  package_uuids: [],
  host_ids: isMyCalendlyScopeValue ? [myMeetingsValue] : [],
  invitee_emails: [],
  team_ids: [],
  user_ids: [],
  utm_campaign: [],
  utm_source: [],
  utm_medium: [],
  utm_content: [],
  utm_term: [],
  search_keyword: searchKeyword,
  meeting_intelligence_status: undefined
})

/**
 * Preset chips use query-only date state; filter apply should leave the clean Upcoming chip when
 * the user keeps `period=upcoming` with no filter changes. Missing `meetings_preset` now means
 * Today, so that path must set `meetings_preset=upcoming` explicitly rather than deleting the key.
 */
export const buildMeetingsFilterApplyParams = (params, { presetFlagOn, hasChangedFilters } = {}) => {
  if (!presetFlagOn) return params

  const next = { ...params }

  if (next.period === Periods.UPCOMING && !hasChangedFilters) {
    // Keep the Upcoming chip explicitly; missing meetings_preset resolves to Today.
    next[MEETINGS_PRESET_PARAM] = next.period
    return next
  }

  delete next[MEETINGS_PRESET_PARAM]
  next[UPCOMING_PRESET_PARAM] = UPCOMING_PRESET_OFF

  return next
}

export const searchResetParams = (searchQuery) => ({
  searchIn: [],
  dateRange: { period: undefined, start: undefined, end: undefined },
  inviteeEmails: [],
  eventTypeIds: [],
  status: [],
  schedulingMethod: [],
  role: [],
  query: searchQuery
})

const sortIfArray = (val) => (Array.isArray(val) ? [...val].sort() : val)

const normalizeMeetingsPageSettings = (settings) => {
  const normalizedSettings = settings ?? {}
  return {
    showBuffers: normalizedSettings.showBuffers,
    schedulingMethods: sortIfArray(normalizedSettings.schedulingMethods || [])
  }
}

export const hasMeetingsPageSettingsChanged = (currentSettings, nextSettings) => !isEqual(
  normalizeMeetingsPageSettings(currentSettings),
  normalizeMeetingsPageSettings(nextSettings)
)

const getChangedFilters = (resetFilters, filterParams) => {
  const paramsToCheck = omit(resetFilters, ['query', 'status_explicitly_set', 'search_keyword'])
  return Object.keys(paramsToCheck).filter((filter) => !isEqual(sortIfArray(filterParams[filter]), sortIfArray(paramsToCheck[filter]))
  )
}

export const changedSearchFilters = (filterParams) => getChangedFilters(searchResetParams(), filterParams)

export const changedMeetingFilters = (isMeetingsPage, isMyCalendlyScopeValue, filterParams) => {
  const resetFilters = resetParams(isMeetingsPage, isMyCalendlyScopeValue)
  return getChangedFilters(resetFilters, snakeize(filterParams))
}
